// apps/web/src/pages/SettingsPage.tsx
import React, { useState, useEffect, useMemo, useRef } from "react";

import { apiGet, apiPost } from "../utils/apiClient";
import LoadingInline from "../components/LoadingInline";
import { ToastViewport, useToast } from "../components/Toast";
import { useLocation } from "react-router-dom";
import { RmsFeeConfigSection } from "../components/settings/RmsFeeConfigSection";
import { HelpTip } from "../components/HelpTip";
import { isFreePlanUser, isLightPlanUser } from "../utils/plan";
import { useUserContext } from "../contexts/UserContext";
import { SettingsSupportInfo } from "../components/SettingsSupportInfo";
import { RmsStopStatus, buildRmsStopParts, parseRmsStopStatus, writeCachedRmsStopStatus } from "../utils/rmsStop";

type PassMeta = { exists: boolean; last4?: string };


const PassStatusLabel = ({
    label,
    meta,
}: {
    label: string;
    meta?: PassMeta;
}) => (
    <div className="text-[11px] text-slate-500 -mb-1">
        {label}：{meta?.exists ? ` 設定済み（************${meta.last4}）` : " 未設定"}
    </div>
);

// ★追加：ローディング表示（止まって見える対策）
const Spinner = ({ size = 18 }: { size?: number }) => (
    <div
        className="animate-spin rounded-full border-2 border-slate-300 border-t-slate-700"
        style={{ width: size, height: size }}
        aria-label="loading"
    />
);

const ToggleSwitch = ({
    checked,
    onChange,
    disabled,
    onLabel = "ON",
    offLabel = "OFF",
}: {
    checked: boolean;
    onChange: (v: boolean) => void;
    disabled?: boolean;
    onLabel?: string;
    offLabel?: string;
}) => (
    <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative inline-flex h-7 w-16 items-center rounded-full border transition
      ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
      ${checked ? "bg-blue-600 border-blue-600" : "bg-slate-200 border-slate-300"}
    `}
        aria-pressed={checked}
    >
        <span
            className={`absolute left-2 text-[10px] font-semibold transition
        ${checked ? "text-white" : "text-slate-600"}
      `}
        >
            {checked ? onLabel : ""}
        </span>
        <span
            className={`absolute right-2 text-[10px] font-semibold transition
        ${checked ? "text-white" : "text-slate-600"}
      `}
        >
            {!checked ? offLabel : ""}
        </span>

        <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition
        ${checked ? "translate-x-10" : "translate-x-1"}
      `}
        />
    </button>
);

export default function SettingsPage() {

    const user = useUserContext();

    // ✅ 成功・失敗のお知らせ（共通トースト）
    const { toasts, pushToast, removeToast } = useToast();
    const isFreePlan = useMemo(() => isFreePlanUser(user), [user]);

    const isRestrictedPlan = useMemo(() => isFreePlanUser(user) || isLightPlanUser(user), [user]);

    console.log("★isFreePlan=" + isFreePlan)
    const location = useLocation();
    const [showSetupBanner, setShowSetupBanner] = useState(false);
    const [showLoginErrorBanner, setShowLoginErrorBanner] = useState(false);

    useEffect(() => {
        const sp = new URLSearchParams(location.search);
        const setup = sp.get("setup");

        setShowSetupBanner(setup === "1");
        setShowLoginErrorBanner(setup === "2");
    }, [location.search]);

    // RN-283: 止まっているときの知らせに、止まった理由（R-Login か受注データの取得の認証か）と最後にデータを取れた日時を出す
    // stopped＝本当に止まっているか（ConfigGate と同じ条件）。URL の setup=2 だけで「止まっています」と出さないため
    const [rmsStop, setRmsStop] = useState<RmsStopStatus | null>(null);
    // 理由が分かるまで知らせを出さない（R-Login の文が一瞬出てから受注の文に変わるのを防ぐ）
    const [rmsStopLoaded, setRmsStopLoaded] = useState(false);
    // RN-283（[056]）: URL の setup=2 に関係なく、初期設定を開いたら毎回取り直す
    //   （全ページの警告の「初期設定を開く」やメニューから setup なしで開いても、止まっていれば出す）
    useEffect(() => {
        // 出し直すたびに前の理由・日時を捨てて取り直す
        setRmsStop(null);
        setRmsStopLoaded(false);
        let cancelled = false;
        (async () => {
            try {
                const res = await apiGet("/api/company/datafetch-status");
                if (!res.ok) return;
                const json = await res.json();
                if (cancelled) return;
                setRmsStop(parseRmsStopStatus(json));
            } catch {
                // 取れないときは今までどおり出す（R-Login の文・日時なし）
            } finally {
                if (!cancelled) setRmsStopLoaded(true);
            }
        })();
        return () => {
            cancelled = true;
        };
        // 設定の画面のまま会社を切り替えたときも取り直す
    }, [location.search, user.companyId]);


    // ▼ 外部アカウント
    const [rmsUser, setRmsUser] = useState("");
    const [rmsPass, setRmsPass] = useState("");
    const [rmsPass2, setRmsPass2] = useState("");
    const [rmsPass3, setRmsPass3] = useState("");
    const [rmsPass4, setRmsPass4] = useState("");
    const [rmsPass5, setRmsPass5] = useState("");
    const [csvUser, setCsvUser] = useState("");
    const [csvPass, setCsvPass] = useState("");

    // ★ 状態表示用
    const [passMeta, setPassMeta] = useState<{
        rmsPassMeta1?: PassMeta;
        rmsPassMeta2?: PassMeta;
        rmsPassMeta3?: PassMeta;
        rmsPassMeta4?: PassMeta;
        rmsPassMeta5?: PassMeta;
        csvPassMeta?: PassMeta;
        rakutenMemberPassMeta?: PassMeta;
    }>({});

    const [loading, setLoading] = useState(true);

    // ▼ 会計・税設定
    const [fiscalMonth, setFiscalMonth] = useState<number>(4);
    const [taxType, setTaxType] = useState<string>("税込");
    // ★ 画面は「％」で持つ（例: 10）
    const [taxRatePercent, setTaxRatePercent] = useState<number | null>(10);

    // ▼ 通知設定（最小：dailyReport / fetchError + default.emails）
    const [notifDailyEnabled, setNotifDailyEnabled] = useState<boolean>(true);
    const [notifFetchErrorEnabled, setNotifFetchErrorEnabled] = useState<boolean>(true);

    // ★追加：チャネル別（メール/Chatwork）
    const [notifDailyEmailEnabled, setNotifDailyEmailEnabled] = useState<boolean>(true);
    const [notifDailyChatworkEnabled, setNotifDailyChatworkEnabled] = useState<boolean>(false);
    const [notifFetchErrorEmailEnabled, setNotifFetchErrorEmailEnabled] = useState<boolean>(true);
    const [notifFetchErrorChatworkEnabled, setNotifFetchErrorChatworkEnabled] = useState<boolean>(false);

    // ★追加：Chatwork roomId が入っているか（入っている場合のみ Chatwork UI を出す）
    const [chatworkRoomMeta, setChatworkRoomMeta] = useState<PassMeta | null>(null);

    // ★ 全通知共通の送信先メール
    const [notifEmails, setNotifEmails] = useState<string[]>([]);
    const [notifEmailInput, setNotifEmailInput] = useState<string>("");

    const [savingNotif, setSavingNotif] = useState(false);
    const savingNotifRef = useRef(false);

    // ▼ 初期同期ステータス
    const [initStatus, setInitStatus] = useState<
        "pending" | "success" | "error" | null
    >(null);

    // ▼ RMS 接続チェックステータス
    const [rmsCheckStatus, setRmsCheckStatus] = useState<
        "pending" | "success" | "error" | null
    >(null);
    const [rmsCheckMessage, setRmsCheckMessage] = useState<string>("");

    // ★ 保存中（二度押し防止）
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);

    // ★ 受注データ保存中（二度押し防止）
    const [savingCsv, setSavingCsv] = useState(false);
    const savingCsvRef = useRef(false);

    // ★ 会計・税設定 保存中（二度押し防止）
    const [savingFiscal, setSavingFiscal] = useState(false);
    const savingFiscalRef = useRef(false);

    // ★ 接続チェック中（二度押し防止）
    const [checking, setChecking] = useState(false);
    const checkingRef = useRef(false);

    // ★ 接続チェックの経過秒（止まって見える対策）
    const [checkingElapsedSec, setCheckingElapsedSec] = useState(0);

    // RMSログインチェック（画面表示用の即時結果）
    const [checkResult, setCheckResult] = useState<{ ok: boolean; message: string } | null>(null);

    // ★ 受注DLチェック中（二度押し防止）
    const [checkingOrderDl, setCheckingOrderDl] = useState(false);
    const checkingOrderDlRef = useRef(false);

    // ★ 受注DLチェックの経過秒
    const [checkingOrderDlElapsedSec, setCheckingOrderDlElapsedSec] = useState(0);

    // 受注DLチェック結果
    const [orderDlCheckResult, setOrderDlCheckResult] = useState<{ ok: boolean; message: string } | null>(null);

    const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

    // ★ 通知メール：軽い正規化＆検証（最小）
    const normalizeEmails = (raw: string) => {
        const parts = String(raw)
            .split(/[\s,、;]+/g)
            .map((s) => s.trim())
            .filter(Boolean);

        const isEmailLike = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

        const next: string[] = [];
        for (const p of parts) {
            const v = p.toLowerCase();
            if (!isEmailLike(v)) return { ok: false as const, invalid: p, list: [] as string[] };
            if (!next.includes(v)) next.push(v);
        }
        return { ok: true as const, list: next };
    };

    const persistNotifSettings = async (emails: string[]) => {
        const hasChatwork = !!chatworkRoomMeta?.exists;

        const res = await apiPost("/api/company/settings/shopNotifications", {
            default: { emails },
            dailyReport: {
                enabled: notifDailyEnabled,
                email: { enabled: notifDailyEmailEnabled },
                chatwork: { enabled: hasChatwork ? notifDailyChatworkEnabled : false },
            },
            fetchError: {
                enabled: notifFetchErrorEnabled,
                email: { enabled: notifFetchErrorEmailEnabled },
                chatwork: { enabled: hasChatwork ? notifFetchErrorChatworkEnabled : false },
            },
        });

        if (!res.ok) {
            const txt = await res.text().catch(() => "");
            throw new Error(`保存に失敗しました（${res.status}）\n${txt}`);
        }
    };

    const addNotifEmailsFromInput = async () => {
        const v = notifEmailInput.trim();
        if (!v) return;

        const parsed = normalizeEmails(v);
        if (!parsed.ok) {
            pushToast("error", `メールアドレス形式が不正です: ${parsed.invalid}`, 5000);
            return;
        }

        const merged = [...notifEmails];
        for (const e of parsed.list) {
            if (!merged.includes(e)) merged.push(e);
        }

        try {
            setSavingNotif(true);
            await persistNotifSettings(merged);
            setNotifEmails(merged);
            setNotifEmailInput("");
        } catch (e: any) {
            pushToast("error", e?.message ?? "通知設定の保存に失敗しました", 0);
        } finally {
            setSavingNotif(false);
        }
    };

    const removeNotifEmail = async (email: string) => {
        const next = notifEmails.filter((x) => x !== email);

        try {
            setSavingNotif(true);
            await persistNotifSettings(next);
            setNotifEmails(next);
        } catch (e: any) {
            pushToast("error", e?.message ?? "通知設定の保存に失敗しました", 0);
        } finally {
            setSavingNotif(false);
        }
    };

    /* --------------------------------------------
     * ① 会計・税設定読み込み
     * -------------------------------------------- */
    useEffect(() => {
        let alive = true;

        async function loadShopSettings() {
            if (!user.companyId) return;

            try {
                const res = await apiGet("/api/company/settings/fiscal");
                if (!res.ok) return;

                const json: any = await res.json();
                if (!alive) return;

                if (json.fiscalMonth != null) setFiscalMonth(Number(json.fiscalMonth));
                if (json.taxType) setTaxType(String(json.taxType));

                // DBは 0.1 のまま → 画面は 10（%）
                if (json.taxRate != null) {
                    const r = Number(json.taxRate);
                    if (Number.isFinite(r)) setTaxRatePercent(r * 100);
                }
            } catch (e) {
                console.error(e);
            }
        }

        loadShopSettings();
        return () => {
            alive = false;
        };
    }, [user.companyId]);

    /* --------------------------------------------
     * ② RMS 設定読み込み（＋ initStatus / rmsCheckStatus の取得元）
     * -------------------------------------------- */
    const fetchRmsSettings = async (opts?: { silent?: boolean }) => {
        if (!user.companyId) return;

        try {
            const res = await apiGet("/api/company/settings/rms");
            if (!res.ok) return;

            const json: any = await res.json();

            // 入力欄は常に空
            if (!opts?.silent) {
                if (json.rmsUser) setRmsUser(String(json.rmsUser));
                if (!isRestrictedPlan && json.csvUser) setCsvUser(String(json.csvUser));
            }

            if (json.passMeta) setPassMeta(json.passMeta);
            setInitStatus(json.initStatus ?? null);

            // ★ 接続チェックの状態も追従
            setRmsCheckStatus(json.rmsCheckStatus ?? null);
            setRmsCheckMessage(String(json.rmsCheckMessage ?? ""));

            if (!opts?.silent) {
                setRmsPass("");
                setRmsPass2("");
                setRmsPass3("");
                setRmsPass4("");
                setRmsPass5("");
                setCsvPass("");
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        let alive = true;

        (async () => {
            if (!user.companyId) {
                if (alive) setLoading(false);
                return;
            }

            try {
                await fetchRmsSettings();
            } finally {
                if (alive) setLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.companyId, isRestrictedPlan]);

    /* --------------------------------------------
     * ③ 【最小修正】Firestore購読をやめる
     *    pending の間だけ API をポーリングして status を追従
     * -------------------------------------------- */
    useEffect(() => {
        if (!user.companyId) return;
        if (initStatus !== "pending" && rmsCheckStatus !== "pending") return;

        const timer = window.setInterval(() => {
            // silent: true で入力欄は触らず、status/metaだけ更新
            fetchRmsSettings({ silent: true });
        }, 5000);

        return () => window.clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.companyId, initStatus, rmsCheckStatus]);

    /* --------------------------------------------
     * ★ 接続チェック中の経過秒（UIの“止まった感”を消す）
     * -------------------------------------------- */
    useEffect(() => {
        if (!checking) return;

        setCheckingElapsedSec(0);
        const start = Date.now();
        const t = window.setInterval(() => {
            setCheckingElapsedSec(Math.floor((Date.now() - start) / 1000));
        }, 500);

        return () => window.clearInterval(t);
    }, [checking]);

    /* --------------------------------------------
     * 受注DLチェック中の経過秒
     * -------------------------------------------- */
    useEffect(() => {
        if (!checkingOrderDl) return;

        setCheckingOrderDlElapsedSec(0);
        const start = Date.now();
        const t = window.setInterval(() => {
            setCheckingOrderDlElapsedSec(Math.floor((Date.now() - start) / 1000));
        }, 500);

        return () => window.clearInterval(t);
    }, [checkingOrderDl]);

    /* --------------------------------------------
     * ④-2 RMS 接続チェック（check-wait）
     * -------------------------------------------- */
    async function checkRmsConnection() {
        if (checkingRef.current) return;
        checkingRef.current = true;
        setChecking(true);
        setCheckResult(null);

        // UI即反映
        setRmsCheckStatus("pending");
        setRmsCheckMessage("");

        try {
            const apiPromise = apiPost("/api/rms/check-wait", {
                timeoutMs: 55_000,
                stepMs: 2_000,
            });

            const SOFT_TIMEOUT_MS = 70_000;

            const res: any = await Promise.race([
                apiPromise,
                sleep(SOFT_TIMEOUT_MS).then(() => null),
            ]);

            if (!res) {
                setRmsCheckStatus("pending");
                setRmsCheckMessage("");
                setCheckResult({
                    ok: false,
                    message:
                        "通信が長引いています。接続チェック自体は継続している可能性があります。結果は自動更新されます（約数十秒〜数分）。",
                });

                fetchRmsSettings({ silent: true });
                return;
            }

            const json: any = await res.json().catch(() => null);

            if (!res.ok) {
                const msg = String(json?.message || json?.error || `接続チェックに失敗しました（${res.status}）`);
                setRmsCheckStatus("error");
                setRmsCheckMessage(msg);
                setCheckResult({ ok: false, message: msg });
                return;
            }

            const status = String(json?.status ?? "pending");
            const msg = String(json?.message ?? "");

            if (status === "success") {
                setRmsCheckStatus("success");
                setRmsCheckMessage(msg);
                setCheckResult({ ok: true, message: msg ? `OK（${msg}）` : "OK" });
                fetchRmsSettings({ silent: true });
                return;
            }

            if (status === "error") {
                setRmsCheckStatus("error");
                setRmsCheckMessage(msg);
                setCheckResult({ ok: false, message: msg || "NG" });
                fetchRmsSettings({ silent: true });
                return;
            }

            setRmsCheckStatus("pending");
            setRmsCheckMessage("");
            setCheckResult({
                ok: false,
                message: "接続チェックを開始しました。完了までしばらくお待ちください（結果は自動更新されます）。",
            });
        } catch (e: any) {
            const msg = e?.message ?? String(e);
            setRmsCheckStatus("error");
            setRmsCheckMessage(msg);
            setCheckResult({ ok: false, message: msg });
        } finally {
            checkingRef.current = false;
            setChecking(false);
        }
    }

    /* --------------------------------------------
     * ④ RMS 保存（保存後に毎回、接続チェックを実行）
     * -------------------------------------------- */
    async function save() {
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);

        const body: Record<string, any> = {
            rmsUser,
        };

        if (rmsPass) body.rmsPass = rmsPass;
        if (rmsPass2) body.rmsPass2 = rmsPass2;
        if (rmsPass3) body.rmsPass3 = rmsPass3;
        if (rmsPass4) body.rmsPass4 = rmsPass4;
        if (rmsPass5) body.rmsPass5 = rmsPass5;

        let savedOk = false;

        try {
            const res = await apiPost("/api/company/settings/rms", body);

            if (!res.ok) {
                const txt = await res.text().catch(() => "");
                pushToast("error", `保存に失敗しました（${res.status}）\n${txt}`, 0);
                return;
            }

            savedOk = true;
            // RN-283: 保存できたら、止まっている知らせを下げる（保存で止まっている印が外れる）
            setShowLoginErrorBanner(false);
            setRmsStop((prev) => (prev ? { ...prev, stopped: false } : prev));
            // 全ページの警告の控えも捨てる（次のページで古い警告が一瞬出ないように）
            writeCachedRmsStopStatus(user.companyId, null);

            setInitStatus("pending");
            localStorage.removeItem("initialSetupGuideDone");

            setRmsPass("");
            setRmsPass2("");
            setRmsPass3("");
            setRmsPass4("");
            setRmsPass5("");

            fetchRmsSettings({ silent: true });
        } finally {
            savingRef.current = false;
            setSaving(false);
        }

        if (savedOk) {
            pushToast("success", "保存しました。RMS認証エラーがある場合もクリアされます。続けてRMS接続チェックを実行します。", 6000);
            await checkRmsConnection();
        }
    }

    /* --------------------------------------------
     * ④-3 受注データ保存（RMSチェックなし）
     * -------------------------------------------- */
    async function saveCsv() {
        if (savingCsvRef.current) return;
        savingCsvRef.current = true;
        setSavingCsv(true);

        const body: Record<string, any> = {
            csvUser,
        };
        if (csvPass) body.csvPass = csvPass;

        let savedOk = false;

        try {
            const res = await apiPost("/api/company/settings/rms", body);

            if (!res.ok) {
                const txt = await res.text().catch(() => "");
                pushToast("error", `保存に失敗しました（${res.status}）\n${txt}`, 0);
                return;
            }

            savedOk = true;
            // RN-283: 保存できたら、止まっている知らせを下げる（保存で止まっている印が外れる）
            setShowLoginErrorBanner(false);
            setRmsStop((prev) => (prev ? { ...prev, stopped: false } : prev));
            // 全ページの警告の控えも捨てる（次のページで古い警告が一瞬出ないように）
            writeCachedRmsStopStatus(user.companyId, null);
            setCsvPass("");
            fetchRmsSettings({ silent: true });
        } finally {
            savingCsvRef.current = false;
            setSavingCsv(false);
        }

        if (savedOk) {
            pushToast("success", "保存しました。続けて受注DLチェックを実行します。", 6000);
            await checkOrderDlCredentials();
        }
    }

    /* --------------------------------------------
     * ④-3 受注DLユーザー名・パスワードチェック
     * -------------------------------------------- */
    async function checkOrderDlCredentials() {
        if (checkingOrderDlRef.current) return;
        checkingOrderDlRef.current = true;
        setCheckingOrderDl(true);
        setOrderDlCheckResult(null);

        try {
            const res = await apiPost("/api/rms/order-dl-check-wait", {});
            const json: any = await res.json().catch(() => null);

            if (!res.ok) {
                const msg = String(json?.message || json?.error || `チェックに失敗しました（${res.status}）`);
                setOrderDlCheckResult({ ok: false, message: msg });
                return;
            }

            const status = String(json?.status ?? "error");
            const msg = String(json?.message ?? "");

            if (status === "success") {
                setOrderDlCheckResult({ ok: true, message: "OK（認証が確認できました）" });
                return;
            }

            setOrderDlCheckResult({ ok: false, message: msg || "チェックに失敗しました" });
        } catch (e: any) {
            setOrderDlCheckResult({ ok: false, message: e?.message ?? String(e) });
        } finally {
            checkingOrderDlRef.current = false;
            setCheckingOrderDl(false);
        }
    }

    /* --------------------------------------------
     * ⑤ 会計・税設定保存
     * -------------------------------------------- */
    async function saveAccountingSettings() {
        if (savingFiscalRef.current) return;
        savingFiscalRef.current = true;
        setSavingFiscal(true);

        const taxRate = taxRatePercent == null ? null : Number(taxRatePercent) / 100;

        try {
            const res = await apiPost("/api/company/settings/fiscal", {
                fiscalMonth,
                taxType,
                taxRate,
            });

            if (!res.ok) {
                const txt = await res.text().catch(() => "");
                pushToast("error", `保存に失敗しました（${res.status}）\n${txt}`, 0);
                return;
            }

            pushToast("success", "会計・税設定を保存しました");
        } finally {
            savingFiscalRef.current = false;
            setSavingFiscal(false);
        }
    }

    /* --------------------------------------------
     * ⑥ 通知設定読み込み（shopNotifications）
     * -------------------------------------------- */
    useEffect(() => {
        let alive = true;

        (async () => {
            if (!user.companyId) return;

            try {
                const res = await apiGet("/api/company/settings/shopNotifications");
                if (!res.ok) return;

                const json: any = await res.json();
                if (!alive) return;

                if (json?.dailyReport?.enabled != null) setNotifDailyEnabled(!!json.dailyReport.enabled);
                if (json?.fetchError?.enabled != null) setNotifFetchErrorEnabled(!!json.fetchError.enabled);

                // ★追加：チャネル別 enabled（存在するものだけ反映）
                if (json?.dailyReport?.email?.enabled != null) setNotifDailyEmailEnabled(!!json.dailyReport.email.enabled);
                if (json?.dailyReport?.chatwork?.enabled != null) setNotifDailyChatworkEnabled(!!json.dailyReport.chatwork.enabled);

                if (json?.fetchError?.email?.enabled != null) setNotifFetchErrorEmailEnabled(!!json.fetchError.email.enabled);
                if (json?.fetchError?.chatwork?.enabled != null) setNotifFetchErrorChatworkEnabled(!!json.fetchError.chatwork.enabled);

                // ★追加：Chatwork room meta（exists のときだけUI表示）
                if (json?.chatworkRoomMeta && typeof json.chatworkRoomMeta === "object") {
                    const exists = !!json.chatworkRoomMeta.exists;
                    const last4 = json.chatworkRoomMeta.last4 != null ? String(json.chatworkRoomMeta.last4) : undefined;
                    setChatworkRoomMeta({ exists, last4 });
                    // room 無いなら chatwork トグルはOFFに寄せる（見えないけど念のため）
                    if (!exists) {
                        setNotifDailyChatworkEnabled(false);
                        setNotifFetchErrorChatworkEnabled(false);
                    }
                } else {
                    setChatworkRoomMeta(null);
                    setNotifDailyChatworkEnabled(false);
                    setNotifFetchErrorChatworkEnabled(false);
                }

                const emails =
                    (Array.isArray(json?.default?.emails) && json.default.emails) ||
                    (typeof json?.default?.email === "string" ? [json.default.email] : null) ||
                    (Array.isArray(json?.emails) && json.emails) ||
                    (typeof json?.email === "string" ? [json.email] : null) ||
                    [];

                const cleaned = emails
                    .map((x: any) => String(x).trim().toLowerCase())
                    .filter((x: string) => x && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x));

                const uniq: string[] = [];
                for (const e of cleaned) if (!uniq.includes(e)) uniq.push(e);

                setNotifEmails(uniq);
            } catch (e) {
                console.error(e);
            }
        })();

        return () => {
            alive = false;
        };
    }, [user.companyId]);

    /* --------------------------------------------
     * ⑦ 通知設定保存（shopNotifications）
     * -------------------------------------------- */
    async function saveShopNotifications() {
        if (savingNotifRef.current) return;
        savingNotifRef.current = true;
        setSavingNotif(true);

        try {
            let nextEmails = [...notifEmails];
            const raw = notifEmailInput.trim();

            // 入力欄に残っているメールも保存時に取り込む
            if (raw) {
                const parsed = normalizeEmails(raw);
                if (!parsed.ok) {
                    pushToast("error", `メールアドレス形式が不正です: ${parsed.invalid}`, 5000);
                    return;
                }
                for (const e of parsed.list) {
                    if (!nextEmails.includes(e)) nextEmails.push(e);
                }
            }

            await persistNotifSettings(nextEmails);

            setNotifEmails(nextEmails);
            setNotifEmailInput("");

            pushToast("success", "通知設定を保存しました");
        } finally {
            savingNotifRef.current = false;
            setSavingNotif(false);
        }
    }

    if (loading) return <LoadingInline />;

    const progressPct = Math.min(100, Math.floor((checkingElapsedSec / 60) * 100));
    const orderDlProgressPct = Math.min(100, Math.floor((checkingOrderDlElapsedSec / 120) * 100));
    const hasChatwork = !!chatworkRoomMeta?.exists;

    return (
        <div className="mx-auto w-full max-w-6xl text-sm">
            {/* ✅ トースト */}
            <ToastViewport toasts={toasts} onClose={removeToast} />

            {/* 取れたときは本当に止まっているときだけ出す（setup に関係なく）。取れなかったときは setup=2 のときだけ今までどおり出す */}
            {rmsStopLoaded && (rmsStop ? rmsStop.stopped : showLoginErrorBanner) && (
                <div role="alert" className="mb-4 overflow-hidden rounded-xl border-2 border-red-600 shadow-md">
                    {/* RN-283: 止まった理由ごとの文（「有効期限が切れている可能性」は使わない）。
                        アクセスのたびに経由する画面なので、ほかの案内（薄い色の箱）と見分けがつくよう、
                        1文目（止まっていること）は赤の帯に白の太字で出す */}
                    <div className="flex items-start gap-2 bg-red-600 px-4 py-2.5 text-white">
                        <span aria-hidden="true" className="text-[15px] leading-6">⚠</span>
                        <div className="text-[15px] font-bold leading-6">
                            {buildRmsStopParts(rmsStop, { onSettingsPage: true }).headline}
                        </div>
                    </div>
                    <div className="bg-red-50 px-4 py-2.5 pl-10 text-[13px] text-red-900">
                        {buildRmsStopParts(rmsStop, { onSettingsPage: true }).body}
                    </div>
                </div>
            )}

            {showSetupBanner && !isRestrictedPlan && (
                <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900">
                    初期設定が必要です。RMSログイン設定 / 受注データ取得 / 会計・税設定を完了してください。
                </div>
            )}
            {showSetupBanner && isRestrictedPlan && (
                <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900">
                    初期設定が必要です。RMSログイン設定 / 会計・税設定を完了してください。
                </div>
            )}
            {checking && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-3 shadow-md w-[340px]">
                        <div className="flex items-center gap-3">
                            <Spinner size={18} />
                            <div className="font-medium text-[13px]">RMS接続チェック中…</div>
                        </div>

                        <div className="mt-2 text-[12px] text-slate-600 leading-relaxed">
                            最大1分ほどかかることがあります。
                            <br />
                            画面を閉じずにお待ちください。
                        </div>

                        <div className="mt-3">
                            <div className="h-2 rounded bg-slate-200 overflow-hidden">
                                <div
                                    className="h-2 bg-slate-700 transition-all"
                                    style={{ width: `${progressPct}%` }}
                                />
                            </div>
                            <div className="mt-2 text-[11px] text-slate-500 flex justify-between">
                                <span>経過: {checkingElapsedSec}s</span>
                                <span>目安: 60s</span>
                            </div>
                            <div className="mt-1 text-[11px] text-slate-400">
                                ※ 通信が戻らない場合でも、結果は自動更新されます（5秒ごとに確認）
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {checkingOrderDl && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-3 shadow-md w-[340px]">
                        <div className="flex items-center gap-3">
                            <Spinner size={18} />
                            <div className="font-medium text-[13px]">受注DLログイン情報チェック中…</div>
                        </div>

                        <div className="mt-2 text-[12px] text-slate-600 leading-relaxed">
                            最大2分ほどかかることがあります。
                            <br />
                            画面を閉じずにお待ちください。
                        </div>

                        <div className="mt-3">
                            <div className="h-2 rounded bg-slate-200 overflow-hidden">
                                <div
                                    className="h-2 bg-slate-700 transition-all"
                                    style={{ width: `${orderDlProgressPct}%` }}
                                />
                            </div>
                            <div className="mt-2 text-[11px] text-slate-500 flex justify-between">
                                <span>経過: {checkingOrderDlElapsedSec}s</span>
                                <span>目安: 120s</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/*
            {initStatus === "pending" && (
                <div className="mb-3 rounded-lg bg-yellow-100 px-3 py-2.5 text-[13px]">
                    初期データを取得しています…
                </div>
            )}
            {initStatus === "success" && (
                <div className="mb-3 rounded-lg bg-green-100 px-3 py-2.5 text-[13px]">
                    初期データの取得が完了しました
                </div>
            )}
            {initStatus === "error" && (
                <div className="mb-3 rounded-lg bg-red-100 px-3 py-2.5 text-[13px]">
                    初期データの取得に失敗しました
                </div>
            )}

            {rmsCheckStatus === "pending" && (
                <div className="mb-3 rounded-lg bg-yellow-100 px-3 py-2.5 text-[13px] flex items-center gap-2">
                    <Spinner size={14} />
                    <span>RMS接続チェック中…</span>
                    <span className="text-[11px] text-slate-500 ml-auto">経過 {checkingElapsedSec}s</span>
                </div>
            )}
            {rmsCheckStatus === "success" && (
                <div className="mb-3 rounded-lg bg-green-100 px-3 py-2.5 text-[13px]">
                    RMS接続チェック：OK
                    {rmsCheckMessage ? `（${rmsCheckMessage}）` : ""}
                </div>
            )}
            {rmsCheckStatus === "error" && (
                <div className="mb-3 rounded-lg bg-red-100 px-3 py-2.5 text-[13px]">
                    RMS接続チェック：NG
                    {rmsCheckMessage ? `（${rmsCheckMessage}）` : ""}
                </div>
            )}
*/}
            {/* RN-283: 広い画面では左＝設定の入力、右＝案内（スタッフ登録・サポート情報・使い方）。
                狭い画面では、設定 → 案内の順に縦に並ぶ。止まっているときの赤い知らせは、この上の全幅に出す */}
            <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start lg:gap-5">
                <div className="min-w-0">
                    {/* 狭い画面（右の列が下に回るとき）は、入力の前に読んでほしいスタッフ登録の案内だけを上に出す */}
                    <div className="mb-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[13px] shadow-sm lg:hidden">
                        <div className="font-medium text-slate-800 mb-1">
                            【重要】RMSスタッフ登録が必要です
                        </div>
                        <div className="text-slate-600 leading-relaxed">
                            ec vision の各サービスをご利用いただくには、
                            <br />
                            <strong>RMSにて「スタッフ登録」</strong>を行い、
                            そのログイン情報を本画面に入力していただく必要があります。
                        </div>
                        <div className="mt-2">
                            <a
                                href="https://ec-vision.co.jp/blog/addstaff/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-700 underline font-medium"
                            >
                                ▶ RMSスタッフ登録の手順はこちら
                            </a>
                        </div>
                    </div>
                    {/* ▼ RMS */}
                    <div className="border p-3 rounded-xl bg-white shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                            <h2 className="text-base font-medium leading-none">RMSログイン設定</h2>

                            <div className="flex items-center">
                                <HelpTip
                                    text={
                                        <>
                                            R-Login ID / パスワードについて<br />
                                            RMSにログインする際の「1回目のR-Login ID」と「パスワード」を入力してください。<br />
                                            パスワードを設定すると、「未設定」から「●●●」のように末尾のみ表示されます。<br />
                                            <br />
                                            R-Login パスワード②〜⑤について<br />
                                            RMSのR-Loginパスワードは約2週間ごとに変更されます。<br />
                                            あらかじめ今後のパスワードが決まっている場合は②〜⑤に登録することで、<br />
                                            最大約70日間（2週間 × 5回）、ログインエラーを防ぐことができます。<br />
                                            <br />
                                            ※②〜⑤の登録は任意です。
                                        </>
                                    }
                                />
                            </div>
                        </div>

                        <div className="flex flex-col gap-2.5">
                            <div className="text-[11px] text-slate-500 -mt-1">
                                ※ RMSにログインする際の１回目IDとパスワードを入力してください
                            </div>
                            <div className="text-[11px] text-slate-500 -mb-1">R-Login ID</div>
                            <input
                                type="text"
                                placeholder="R-Login ID"
                                value={rmsUser}
                                onChange={(e) => setRmsUser(e.target.value)}
                                className="border rounded px-2 py-1"
                            />

                            <PassStatusLabel label="R-Login パスワード" meta={passMeta.rmsPassMeta1} />
                            <input
                                type="password"
                                placeholder="変更する場合のみ入力"
                                value={rmsPass}
                                onChange={(e) => setRmsPass(e.target.value)}
                                className="border rounded px-2 py-1"
                            />
                            <div className="text-[11px] text-slate-500 -mt-1">
                                ※ 今後のパスワードを②～⑤に登録しておくことで、変更後もログインエラーを防ぐことができます。
                            </div>
                            <PassStatusLabel label="R-Login パスワード②" meta={passMeta.rmsPassMeta2} />
                            <input
                                type="password"
                                placeholder="変更する場合のみ入力"
                                value={rmsPass2}
                                onChange={(e) => setRmsPass2(e.target.value)}
                                className="border rounded px-2 py-1"
                            />

                            <PassStatusLabel label="R-Login パスワード③" meta={passMeta.rmsPassMeta3} />
                            <input
                                type="password"
                                placeholder="変更する場合のみ入力"
                                value={rmsPass3}
                                onChange={(e) => setRmsPass3(e.target.value)}
                                className="border rounded px-2 py-1"
                            />

                            <PassStatusLabel label="R-Login パスワード④" meta={passMeta.rmsPassMeta4} />
                            <input
                                type="password"
                                placeholder="変更する場合のみ入力"
                                value={rmsPass4}
                                onChange={(e) => setRmsPass4(e.target.value)}
                                className="border rounded px-2 py-1"
                            />

                            <PassStatusLabel label="R-Login パスワード⑤" meta={passMeta.rmsPassMeta5} />
                            <input
                                type="password"
                                placeholder="変更する場合のみ入力"
                                value={rmsPass5}
                                onChange={(e) => setRmsPass5(e.target.value)}
                                className="border rounded px-2 py-1"
                            />

                            <button
                                onClick={save}
                                disabled={saving || checking}
                                className={`bg-blue-600 text-white px-4 py-1.5 rounded w-full text-sm ${saving || checking ? "opacity-60 cursor-not-allowed" : ""
                                    }`}
                            >
                                {saving ? "保存中…" : "保存（保存後にRMSログインチェック）"}
                            </button>

                            <button
                                onClick={checkRmsConnection}
                                disabled={checking}
                                className={`bg-slate-700 text-white px-4 py-1.5 rounded w-full text-sm ${checking ? "opacity-60 cursor-not-allowed" : ""}`}
                            >
                                {checking ? "接続チェック中…" : "接続チェック（保存済み情報でRMSログインチェック）"}
                            </button>

                            {checkResult && (
                                <div className={`mt-2 rounded-lg px-3 py-2.5 text-[13px] ${checkResult.ok ? "bg-green-100" : "bg-red-100"}`}>
                                    {checkResult.message}
                                </div>
                            )}

                            <div className="text-[11px] text-slate-500 -mt-1">
                                ※ 受注データ データダウンロード用のユーザ名、パスワードは接続チェックの対象外です。<br />
                                ※ 接続チェックは「保存済みの情報」で実行されます（入力中の内容で試したい場合は先に保存してください）
                            </div>

                            {/* ▼ CSV（フリープランは不要） */}
                            {!isRestrictedPlan && (
                                <>
                                    <div className="mt-2 border-t pt-2"></div>

                                    <div className="flex items-center gap-2 mb-3">
                                        <h2 className="text-base font-medium mb-2">
                                            受注データ データダウンロード用のユーザ名、パスワードを入力してください。
                                        </h2>

                                        <div className="flex items-center">
                                            <HelpTip
                                                text={
                                                    <>
                                                        受注データダウンロードサービスとは？<br />
                                                        楽天の受注データをCSVファイルでダウンロードするために利用するサービスです。<br />
                                                        （このID・パスワードは、RMSのログイン情報とは異なります。）<br />
                                                        <br />
                                                        ネクストエンジン・クロスモール等の一元管理ツールをお使いの方は利用が必須ですので、既に発行されている情報をご入力ください。<br />
                                                        <br />
                                                        楽天RMSのみで受注管理されている方は、サービスにお申込みされていない可能性もございます。下記の方法でご確認ください。<br />
                                                        <br />
                                                        ① 受注データダウンロードサービスの申込み状況の確認・新規申込みはこちらRMSにログインした状態で以下のページを開いてください。<br />
                                                        👉 受注データダウンロードサービス申込みページ（https://csvdl-rp.rms.rakuten.co.jp/rms/mall/csvdl/CD01_01_001）<br />
                                                        ※「データダウンロード」のページに遷移する方は既に申込済です。<br />
                                                        <br />
                                                        ② ID・パスワードを忘れた場合はこちら<br />
                                                        👉 ID・パスワード再発行ページ（https://navi-manual.faq.rakuten.net/order/000038581?scid=wi_man_syncsearch）<br />
                                                        <br />
                                                        ③ 詳細はこちら<br />
                                                        👉 受注データダウンロードサービスの詳細はこちら（https://navi-manual.faq.rakuten.net/order/000038098?scid=wi_man_syncsearch）
                                                    </>
                                                }
                                            />
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-2">
                                        <div className="text-[11px] text-slate-500 -mb-1">ユーザ名</div>
                                        <input
                                            type="text"
                                            placeholder="ユーザ名"
                                            value={csvUser}
                                            onChange={(e) => setCsvUser(e.target.value)}
                                            className="border rounded px-2 py-1"
                                        />

                                        <PassStatusLabel label="パスワード" meta={passMeta.csvPassMeta} />
                                        <input
                                            type="password"
                                            placeholder="変更する場合のみ入力"
                                            value={csvPass}
                                            onChange={(e) => setCsvPass(e.target.value)}
                                            className="border rounded px-2 py-1"
                                        />

                                        <button
                                            onClick={saveCsv}
                                            disabled={savingCsv || checkingOrderDl}
                                            className={`bg-blue-600 text-white px-4 py-1.5 rounded w-full text-sm ${savingCsv || checkingOrderDl ? "opacity-60 cursor-not-allowed" : ""}`}
                                        >
                                            {savingCsv ? "保存中…" : "保存（保存後に受注DLチェック）"}
                                        </button>

                                        <button
                                            onClick={checkOrderDlCredentials}
                                            disabled={checkingOrderDl || savingCsv}
                                            className={`bg-slate-700 text-white px-4 py-1.5 rounded w-full text-sm ${checkingOrderDl || savingCsv ? "opacity-60 cursor-not-allowed" : ""}`}
                                        >
                                            {checkingOrderDl ? "チェック中…" : "受注DLログイン情報をチェック（保存済み情報でチェック）"}
                                        </button>

                                        {orderDlCheckResult && (
                                            <div className={`mt-1 rounded-lg px-3 py-2.5 text-[13px] ${orderDlCheckResult.ok ? "bg-green-100" : "bg-red-100"}`}>
                                                {orderDlCheckResult.message}
                                            </div>
                                        )}

                                        <div className="text-[11px] text-slate-500 -mt-1">
                                            ※ チェックは「保存済みの情報」で実行されます（入力中の内容で試したい場合は先に保存してください）
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* ▼ 会計・税設定 */}
                    <div className="border p-3 rounded-xl bg-white shadow-sm mt-4">
                        <h2 className="text-base font-medium mb-3">会計・税設定（今期）</h2>

                        <div className="flex flex-col gap-2.5">
                            <div className="flex flex-col gap-1">
                                <label htmlFor="fiscal-start-month" className="text-sm text-slate-700">
                                    決算開始月
                                </label>
                                <select
                                    id="fiscal-start-month"
                                    value={fiscalMonth}
                                    onChange={(e) => setFiscalMonth(Number(e.target.value))}
                                    className="border rounded px-2 py-1 w-40"
                                >
                                    {[...Array(12)].map((_, i) => (
                                        <option key={i + 1} value={i + 1}>
                                            {i + 1}月開始
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <fieldset className="flex flex-col gap-1 border-0 p-0 m-0">
                                <legend className="text-sm text-slate-700 p-0">金額の税表示</legend>
                                <div className="flex gap-6">
                                    <label className="text-sm">
                                        <input
                                            type="radio"
                                            value="税込"
                                            checked={taxType === "税込"}
                                            onChange={(e) => setTaxType(e.target.value)}
                                        />{" "}
                                        税込
                                    </label>
                                    <label className="text-sm">
                                        <input
                                            type="radio"
                                            value="税抜"
                                            checked={taxType === "税抜"}
                                            onChange={(e) => setTaxType(e.target.value)}
                                        />{" "}
                                        税抜
                                    </label>
                                </div>
                                <p className="text-xs text-slate-500 m-0">
                                    「税抜」を選ぶと、日次・月次・商品別の利益を税抜の金額で表示します。費用は各欄に書いてあるとおりに入れてください。
                                </p>
                            </fieldset>

                            <div className="flex flex-col gap-1">
                                <label htmlFor="tax-rate-percent" className="text-sm text-slate-700">
                                    消費税率
                                </label>
                                <div className="flex items-center gap-2">
                                    <input
                                        id="tax-rate-percent"
                                        type="number"
                                        step="0.01"
                                        value={taxRatePercent ?? ""}
                                        onChange={(e) =>
                                            setTaxRatePercent(e.target.value === "" ? null : Number(e.target.value))
                                        }
                                        className="border rounded px-2 py-1 w-40 text-right"
                                    />
                                    <span className="text-sm text-slate-600">%</span>
                                </div>
                            </div>

                            <button
                                onClick={saveAccountingSettings}
                                className={`bg-blue-600 text-white px-4 py-1.5 rounded w-full text-sm ${savingFiscal ? "opacity-60 cursor-not-allowed" : ""
                                    }`}
                            >
                                {savingFiscal ? "保存中…" : "保存"}
                            </button>
                        </div>
                    </div>

                    {/* ▼ 通知設定 */}
                    <div className="border p-3 rounded-xl bg-white shadow-sm mt-4">
                        <h2 className="text-base font-medium mb-3">通知設定</h2>

                        <div className="flex flex-col gap-3">
                            {/* 通知先メール */}
                            <div className="rounded-xl border bg-white p-5">
                                <div className="text-[15px] font-medium text-slate-900">
                                    通知先メール（全通知共通）
                                </div>
                                <div className="mt-1 text-[12px] text-slate-500">
                                    カンマ/改行で複数入力できます（例: owner@example.com, staff@example.com）
                                </div>
                                <div className="mt-0.5 text-[11px] text-slate-400">
                                    ※ メールアドレスの追加・削除は自動保存されます
                                </div>

                                <div className="mt-3 flex gap-2">
                                    <input
                                        type="text"
                                        placeholder="メールアドレスを入力（複数可）"
                                        value={notifEmailInput}
                                        onChange={(e) => setNotifEmailInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                addNotifEmailsFromInput();
                                            }
                                        }}
                                        disabled={savingNotif}
                                        className="flex-1 rounded-lg border px-3 py-2 text-[13px]"
                                    />
                                    <button
                                        type="button"
                                        onClick={addNotifEmailsFromInput}
                                        disabled={savingNotif}
                                        className={`rounded-lg bg-slate-700 px-4 py-2 text-[13px] font-medium text-white ${savingNotif ? "opacity-60 cursor-not-allowed" : ""
                                            }`}
                                    >
                                        {savingNotif ? "保存中…" : "登録"}
                                    </button>
                                </div>

                                {notifEmails.length > 0 ? (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {notifEmails.map((email) => (
                                            <div
                                                key={email}
                                                className="flex items-center gap-2 rounded-full border bg-slate-100 px-3 py-1 text-[12px] text-slate-700"
                                            >
                                                <span>{email}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeNotifEmail(email)}
                                                    disabled={savingNotif}
                                                    className={`text-[15px] leading-none text-slate-400 hover:text-slate-700 ${savingNotif ? "opacity-60 cursor-not-allowed" : ""
                                                        }`}
                                                    aria-label="remove email"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="mt-3 text-[11px] text-slate-500">
                                        ※ 未設定の場合、通知は送信されません
                                    </div>
                                )}
                            </div>

                            {/* 日次損益レポート通知 */}
                            <div className="overflow-hidden rounded-xl border bg-white">
                                <div className="flex items-center justify-between px-6 py-4">
                                    <div className="flex-1 pr-4">
                                        <div className="text-[15px] font-medium text-slate-900">
                                            日次損益レポートの通知設定
                                        </div>
                                        <div className="mt-1 text-[12px] text-slate-500">
                                            毎日お送りしている損益計算レポートの送信の通知を設定します。
                                        </div>
                                    </div>

                                    <div className="flex shrink-0 items-center gap-3">
                                        <span
                                            className={`min-w-[48px] text-right text-[13px] font-medium ${notifDailyEnabled ? "text-blue-600" : "text-slate-400"
                                                }`}
                                        >
                                            {notifDailyEnabled ? "通知ON" : "通知OFF"}
                                        </span>
                                        <ToggleSwitch
                                            checked={notifDailyEnabled}
                                            onChange={setNotifDailyEnabled}
                                        />
                                    </div>
                                </div>

                                <div
                                    className={`border-t bg-slate-50 px-6 py-4 ${!notifDailyEnabled ? "pointer-events-none opacity-40" : ""
                                        }`}
                                >
                                    <div className="mb-3 text-[11px] font-medium tracking-[0.05em] text-slate-400">
                                        通知先チャンネル
                                    </div>

                                    <div className="mb-2 flex items-center justify-between rounded-lg border bg-white px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-[30px] w-[30px] items-center justify-center rounded-md bg-blue-100 text-blue-700">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.8"
                                                    className="h-4 w-4"
                                                    aria-hidden="true"
                                                >
                                                    <rect x="3" y="5" width="18" height="14" rx="2"></rect>
                                                    <path d="M4 7l8 6 8-6"></path>
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-[13px] font-medium text-slate-900">メール</div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <span
                                                className={`min-w-[24px] text-right text-[12px] font-medium ${notifDailyEmailEnabled ? "text-blue-600" : "text-slate-400"
                                                    }`}
                                            >
                                                {notifDailyEmailEnabled ? "ON" : "OFF"}
                                            </span>
                                            <ToggleSwitch
                                                checked={notifDailyEmailEnabled}
                                                onChange={setNotifDailyEmailEnabled}
                                                disabled={!notifDailyEnabled}
                                            />
                                        </div>
                                    </div>

                                    {hasChatwork && (
                                        <div className="flex items-center justify-between rounded-lg border bg-white px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-[30px] w-[30px] items-center justify-center rounded-md bg-green-100 text-[13px] font-medium text-green-700">
                                                    CW
                                                </div>
                                                <div>
                                                    <div className="text-[13px] font-medium text-slate-900">Chatwork</div>
                                                    <div className="mt-[1px] text-[11px] text-slate-400">
                                                        {chatworkRoomMeta?.last4 ? `****${chatworkRoomMeta.last4}` : ""}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-3">
                                                <span
                                                    className={`min-w-[24px] text-right text-[12px] font-medium ${notifDailyChatworkEnabled ? "text-blue-600" : "text-slate-400"
                                                        }`}
                                                >
                                                    {notifDailyChatworkEnabled ? "ON" : "OFF"}
                                                </span>
                                                <ToggleSwitch
                                                    checked={notifDailyChatworkEnabled}
                                                    onChange={setNotifDailyChatworkEnabled}
                                                    disabled={!notifDailyEnabled}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {hasChatwork && (
                                        <div className="mt-3 flex items-start gap-2 rounded-lg border bg-white px-3 py-2 text-[11px] leading-relaxed text-slate-500">
                                            <span className="mt-[1px] shrink-0">ℹ</span>
                                            <span>
                                                Chatworkの通知先グループを変更したい場合は、サポートにてご対応します。お気軽に
                                                <a
                                                    href="https://docs.google.com/forms/d/e/1FAIpQLSdrzL9WveXl_qdF3CwmYk6rFgrLDBfjbItdyuvT3pwrdVRveQ/viewform?usp=header"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="ml-1 text-blue-600 hover:underline"
                                                >
                                                    お問い合わせ
                                                </a>
                                                ください。
                                            </span>
                                        </div>
                                    )}

                                    {notifDailyEnabled &&
                                        !notifDailyEmailEnabled &&
                                        (!hasChatwork || !notifDailyChatworkEnabled) && (
                                            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                                                <span>⚠</span>
                                                <span>通知先チャンネルがすべてOFFです。この通知は誰にも届きません。</span>
                                            </div>
                                        )}
                                </div>
                            </div>

                            {/* RMSログインエラー、データ取得エラー通知 */}
                            <div className="overflow-hidden rounded-xl border bg-white">
                                <div className="flex items-center justify-between px-6 py-4">
                                    <div className="flex-1 pr-4">
                                        <div className="text-[15px] font-medium text-slate-900">
                                            RMSログインエラー、データ取得エラー通知設定
                                        </div>
                                        <div className="mt-1 text-[12px] text-slate-500">
                                            RMSログインエラーや各種データ取得のエラー通知を設定します。
                                        </div>
                                    </div>

                                    <div className="flex shrink-0 items-center gap-3">
                                        <span
                                            className={`min-w-[48px] text-right text-[13px] font-medium ${notifFetchErrorEnabled ? "text-blue-600" : "text-slate-400"
                                                }`}
                                        >
                                            {notifFetchErrorEnabled ? "通知ON" : "通知OFF"}
                                        </span>
                                        <ToggleSwitch
                                            checked={notifFetchErrorEnabled}
                                            onChange={setNotifFetchErrorEnabled}
                                        />
                                    </div>
                                </div>

                                <div
                                    className={`border-t bg-slate-50 px-6 py-4 ${!notifFetchErrorEnabled ? "pointer-events-none opacity-40" : ""
                                        }`}
                                >
                                    <div className="mb-3 text-[11px] font-medium tracking-[0.05em] text-slate-400">
                                        通知先チャンネル
                                    </div>

                                    <div className="mb-2 flex items-center justify-between rounded-lg border bg-white px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-[30px] w-[30px] items-center justify-center rounded-md bg-blue-100 text-blue-700">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.8"
                                                    className="h-4 w-4"
                                                    aria-hidden="true"
                                                >
                                                    <rect x="3" y="5" width="18" height="14" rx="2"></rect>
                                                    <path d="M4 7l8 6 8-6"></path>
                                                </svg>
                                            </div>
                                            <div>
                                                <div className="text-[13px] font-medium text-slate-900">メール</div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <span
                                                className={`min-w-[24px] text-right text-[12px] font-medium ${notifFetchErrorEmailEnabled ? "text-blue-600" : "text-slate-400"
                                                    }`}
                                            >
                                                {notifFetchErrorEmailEnabled ? "ON" : "OFF"}
                                            </span>
                                            <ToggleSwitch
                                                checked={notifFetchErrorEmailEnabled}
                                                onChange={setNotifFetchErrorEmailEnabled}
                                                disabled={!notifFetchErrorEnabled}
                                            />
                                        </div>
                                    </div>

                                    {hasChatwork && (
                                        <div className="flex items-center justify-between rounded-lg border bg-white px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-[30px] w-[30px] items-center justify-center rounded-md bg-green-100 text-[13px] font-medium text-green-700">
                                                    CW
                                                </div>
                                                <div>
                                                    <div className="text-[13px] font-medium text-slate-900">Chatwork</div>
                                                    <div className="mt-[1px] text-[11px] text-slate-400">
                                                        {chatworkRoomMeta?.last4 ? `****${chatworkRoomMeta.last4}` : ""}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-3">
                                                <span
                                                    className={`min-w-[24px] text-right text-[12px] font-medium ${notifFetchErrorChatworkEnabled ? "text-blue-600" : "text-slate-400"
                                                        }`}
                                                >
                                                    {notifFetchErrorChatworkEnabled ? "ON" : "OFF"}
                                                </span>
                                                <ToggleSwitch
                                                    checked={notifFetchErrorChatworkEnabled}
                                                    onChange={setNotifFetchErrorChatworkEnabled}
                                                    disabled={!notifFetchErrorEnabled}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {hasChatwork && (
                                        <div className="mt-3 flex items-start gap-2 rounded-lg border bg-white px-3 py-2 text-[11px] leading-relaxed text-slate-500">
                                            <span className="mt-[1px] shrink-0">ℹ</span>
                                            <span>
                                                Chatworkの通知先グループを変更したい場合は、サポートにてご対応します。お気軽に
                                                <a
                                                    href="https://docs.google.com/forms/d/e/1FAIpQLSdrzL9WveXl_qdF3CwmYk6rFgrLDBfjbItdyuvT3pwrdVRveQ/viewform?usp=header"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="ml-1 text-blue-600 hover:underline"
                                                >
                                                    お問い合わせ
                                                </a>
                                                ください。
                                            </span>
                                        </div>
                                    )}

                                    {notifFetchErrorEnabled &&
                                        !notifFetchErrorEmailEnabled &&
                                        (!hasChatwork || !notifFetchErrorChatworkEnabled) && (
                                            <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                                                <span>⚠</span>
                                                <span>通知先チャンネルがすべてOFFです。この通知は誰にも届きません。</span>
                                            </div>
                                        )}
                                </div>
                            </div>

                            <button
                                onClick={saveShopNotifications}
                                disabled={savingNotif}
                                className={`mt-1 w-full rounded-lg bg-blue-600 px-4 py-3 text-sm text-white ${savingNotif ? "opacity-60 cursor-not-allowed" : ""
                                    }`}
                            >
                                {savingNotif ? "保存中…" : "通知ON/OFF設定を保存"}
                            </button>
                        </div>
                    </div>

                    {/* ▼ 手数料・ジャンル設定（追加） */}
                    <RmsFeeConfigSection />
                </div>

                {/* 右の細い列では、サポート情報のタブ（運用のコツ・よくある質問・仕組みを知る）を列の幅に収める */}
                <aside className="mt-4 space-y-3 lg:mt-0 [&_.csup-tabs]:w-full [&_.csup-tab]:flex-1 [&_.csup-tab]:justify-center [&_.csup-tab]:px-2">
                    <div className="hidden rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[13px] shadow-sm lg:block">
                        <div className="font-medium text-slate-800 mb-1">
                            【重要】RMSスタッフ登録が必要です
                        </div>
                        <div className="text-slate-600 leading-relaxed">
                            ec vision の各サービスをご利用いただくには、
                            <br />
                            <strong>RMSにて「スタッフ登録」</strong>を行い、
                            そのログイン情報を本画面に入力していただく必要があります。
                        </div>
                        <div className="mt-2">
                            <a
                                href="https://ec-vision.co.jp/blog/addstaff/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-700 underline font-medium"
                            >
                                ▶ RMSスタッフ登録の手順はこちら
                            </a>
                        </div>
                    </div>

                    <SettingsSupportInfo sectionId="settings-support-section" />
                </aside>
            </div>
        </div >
    );
}